import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';

export interface MultiSelectFilterOption<T extends string = string> {
  value: T;
  label: string;
}

/** Special value used to represent "no value" / blank entries */
export const BLANK_FILTER_VALUE = '__blank__';

export interface MultiSelectFilterProps<T extends string = string> {
  /** All available options */
  options: MultiSelectFilterOption<T>[];
  /** Currently selected values */
  selected: T[];
  /** Callback when selection changes */
  onChange: (selected: T[]) => void;
  /** Label shown when all items are selected (e.g., "All statuses") */
  allLabel: string;
  /** Label shown when multiple items are selected (e.g., "{{count}} statuses") - uses i18n interpolation */
  countLabel: string;
  /** Whether the popover is open (controlled) */
  open?: boolean;
  /** Callback when popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Additional class name for the trigger button */
  className?: string;
  /** Width of the trigger button */
  width?: string;
  /** Include a "No value" option for filtering items with blank/null values */
  includeBlankOption?: boolean;
  /** Show a search input to filter options (useful for long lists) */
  searchable?: boolean;
  /**
   * Accessible name for the trigger. Needed wherever the control sits under a
   * form `Label` rather than in a filter bar: the trigger only announces its
   * current VALUE ("All departments"), which tells a screen-reader user nothing
   * about which field they are in.
   */
  'aria-label'?: string;
}

/**
 * A multi-select filter dropdown with "All" and "None" shortcuts.
 *
 * Features:
 * - Checkbox list for selecting multiple options
 * - "All" button to select all options
 * - "None" button to clear selection
 * - Shows "All {type}" when nothing or everything is selected
 * - Shows single item label when one item is selected
 * - Shows "{count} {type}" when multiple (but not all) items are selected
 */
export function MultiSelectFilter<T extends string = string>({
  options,
  selected,
  onChange,
  allLabel,
  countLabel,
  open,
  onOpenChange,
  className,
  width = 'w-[160px]',
  includeBlankOption = false,
  searchable = false,
  'aria-label': ariaLabel,
}: MultiSelectFilterProps<T>) {
  const { t } = useTranslation('common');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const handleOpenChange = (value: boolean) => {
    setInternalOpen(value);
    onOpenChange?.(value);
  };

  // Build the full list of values including blank option if enabled
  const allValues = useMemo(() => {
    const values = options.map(o => o.value);
    if (includeBlankOption) {
      values.unshift(BLANK_FILTER_VALUE as T);
    }
    return values;
  }, [options, includeBlankOption]);

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, searchQuery]);

  // Track if user explicitly clicked "None" - this is a temporary UI state
  // that shows all checkboxes unchecked until any other interaction
  const [showNoneState, setShowNoneState] = useState(false);

  // Reset the "none" state when selected changes externally (e.g., URL change, page load)
  useEffect(() => {
    if (selected.length > 0) {
      setShowNoneState(false);
    }
  }, [selected]);

  // Clear search and focus input when popover opens
  useEffect(() => {
    if (isOpen && searchable) {
      setSearchQuery('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, searchable]);

  const toggleOption = (value: T) => {
    // Phantom "all checked" (empty array === all, when the user hasn't clicked
    // "Clear") must toggle from the FULL set, so clicking a box UNCHECKS it —
    // matching the checkbox affordance. Toggling from the empty array instead
    // would select ONLY the clicked value (the inversion bug).
    const base = selected.length === 0 && !showNoneState ? allValues : selected;
    const newSelected = base.includes(value)
      ? base.filter(v => v !== value)
      : [...base, value];
    // Unchecking the LAST checked box yields [] — which otherwise renders as the
    // phantom "all checked" state again (every box flips back to checked). Treat
    // that exactly like clicking "Clear": stay in the explicit none-state so the
    // boxes remain visually unchecked. Any other toggle clears the none-state.
    setShowNoneState(newSelected.length === 0);
    onChange(newSelected);
  };

  const selectAll = () => {
    setShowNoneState(false);
    onChange(allValues);
  };

  const selectNone = () => {
    setShowNoneState(true); // Mark that user explicitly clicked "None"
    onChange([]);
  };

  // Get display label based on selection state
  const getDisplayLabel = (): string => {
    // Nothing selected OR everything selected = show "All {type}"
    if (selected.length === 0 || selected.length === allValues.length) {
      return allLabel;
    }
    // Single item selected = show that item's label
    if (selected.length === 1) {
      // Check if it's the blank option
      if (selected[0] === BLANK_FILTER_VALUE) {
        return t('app.noValue');
      }
      const option = options.find(o => o.value === selected[0]);
      return option?.label || selected[0];
    }
    // Multiple items selected = show count
    return t(countLabel, { count: selected.length });
  };

  // Both empty (without showNoneState) and full array mean "all" for filtering
  const isAllSelected = (selected.length === 0 && !showNoneState) || selected.length === allValues.length;
  // "All" button disabled when all checkboxes are already shown as checked
  const isAllDisabled = selected.length === allValues.length || (selected.length === 0 && !showNoneState);
  // "None" button disabled when in the "none" state (all unchecked)
  const isNoneDisabled = showNoneState;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={ariaLabel}
          className={`${width} justify-between ${className || ''}`}
        >
          <span className="truncate">{getDisplayLabel()}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ms-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        {/* Search input */}
        {searchable && (
          <div className="p-2 border-b">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('app.search')}
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        )}
        {/* All / Clear shortcuts */}
        <div className="flex items-center justify-between p-2 border-b">
          <Button
            variant={isAllSelected ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 text-xs px-3"
            onClick={selectAll}
            disabled={isAllDisabled}
          >
            {t('app.all')}
          </Button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={selectNone}
            disabled={isNoneDisabled}
          >
            {t('app.clear')}
          </button>
        </div>
        {/* Options list */}
        <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto">
          {/* Blank option at the top if enabled */}
          {includeBlankOption && !searchQuery && (
            <>
              {(() => {
                const blankValue = BLANK_FILTER_VALUE as T;
                const isBlankSelected = selected.includes(blankValue) ||
                  (selected.length === 0 && !showNoneState);
                return (
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                    onClick={() => toggleOption(blankValue)}
                  >
                    <Checkbox
                      checked={isBlankSelected}
                      onCheckedChange={() => toggleOption(blankValue)}
                    />
                    <span className="text-sm flex-1 italic text-muted-foreground">{t('app.noValue')}</span>
                    {isBlankSelected && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </div>
                );
              })()}
              <div className="border-b my-1" />
            </>
          )}
          {filteredOptions.map((option) => {
            // Show checked if:
            // - explicitly in the selected array, OR
            // - nothing is selected AND user didn't explicitly click "None"
            const isSelected = selected.includes(option.value) ||
              (selected.length === 0 && !showNoneState);
            return (
              <div
                key={option.value}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                onClick={() => toggleOption(option.value)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleOption(option.value)}
                />
                <span className="text-sm flex-1">{option.label}</span>
                {isSelected && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}
              </div>
            );
          })}
          {searchable && filteredOptions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">{t('app.noResults')}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
